/**
 * Dynamic Style Guide
 * Client-side filtering + "View more". Options populated from card data attributes.
 * Multi-select checkbox filters styled like facets.
 */

import AOS from 'aos';
import MobileFilterDrawer from '../components/MobileFilterDrawer';
import { openQuickshop } from '../helpers';

const STORAGE_KEY = 'style-guide-compare-ids';

const selectors = {
  filterGroup: '[data-style-filter]',
  filterOptions: '[data-style-filter-options]',
  grid: '[data-style-guide-grid]',
  card: '[data-style-card]',
  quickshopTrigger: '[data-quickshop-trigger]',
  addToCompare: '[data-add-to-compare]',
  showComparisons: '[data-show-comparisons]',
  lazyImg: '[data-style-img]',
  hoverVideo: '[data-hover-video]',
  hoverRotationHint: '[data-hover-rotation-hint]',
  noResults: '[data-style-guide-no-results]',
  viewMore: '[data-style-guide-view-more]',
  viewMoreBtn: '[data-style-guide-view-more-btn]',
  viewingNum: '[data-sizing-viewing-num]',
  viewingTotal: '[data-sizing-viewing-total]',
};

const SIZE_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];
const DEFAULT_VISIBLE = 12;

function attrToCamel(attr) {
  return attr.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

function sortBySizeOrder(a, b) {
  const idx = s => {
    const i = SIZE_ORDER.indexOf(s.toUpperCase());
    return i === -1 ? SIZE_ORDER.length : i;
  };
  return idx(a) - idx(b) || a.localeCompare(b);
}

export default class DynamicSizingExamples {
  constructor(section) {
    this.el = section.el;
    this.initialVisible = section.data.initialVisible ?? DEFAULT_VISIBLE;
    this.loadMoreCount = section.data.loadMoreCount ?? DEFAULT_VISIBLE;
    this.visibleLimit = this.initialVisible;

    this.setupFilter();
    this.setupCompare();
    this.setupShowComparisons();
    this.setupQuickshop();
    this.populateFilters();
    this.setupViewMore();
    this.applyVisibility();

    new MobileFilterDrawer(this.el);
  }

  cardMatchesFilters(card, filterValues, excludeAttr) {
    return Object.entries(filterValues).every(([key, value]) => {
      if (excludeAttr && attrToCamel(excludeAttr) === key) {
        return true;
      }
      const selectedValues = Array.isArray(value)
        ? value
        : [value].filter(Boolean);
      if (selectedValues.length === 0) {
        return true;
      }
      const cardValRaw = (card.dataset[key] ?? '').trim();
      if (!cardValRaw) {
        return false;
      }
      // Support comma-separated values (e.g. from product metafield list)
      const cardVals = cardValRaw
        .split(',')
        .map(v => v.trim())
        .filter(Boolean);
      return cardVals.some(cv => selectedValues.includes(cv));
    });
  }

  populateFilters() {
    const grid = this.el.querySelector(selectors.grid);
    const cards = [...(grid?.querySelectorAll(selectors.card) ?? [])];
    const filterValues = this.getFilterValues();

    this.el.querySelectorAll(selectors.filterGroup).forEach(group => {
      const attr = group.dataset.filterAttr;
      if (!attr) {
        return;
      }

      const key = attrToCamel(attr);

      // Collect ALL unique values from ALL cards (no filtering)
      const allValues = new Set();
      cards.forEach(card => {
        const valRaw = (card.dataset[key] ?? '').trim();
        if (valRaw) {
          valRaw
            .split(',')
            .map(v => v.trim())
            .filter(Boolean)
            .forEach(v => allValues.add(v));
        }
      });

      const sorted =
        attr === 'size'
          ? [...allValues].sort(sortBySizeOrder)
          : [...allValues].sort((a, b) => a.localeCompare(b));

      // Determine which values have no matching cards (given other filter selections)
      const disabledValues = new Set();
      sorted.forEach(val => {
        const testFilterValues = { ...filterValues };
        testFilterValues[key] = [val];
        const hasMatch = cards.some(card =>
          this.cardMatchesFilters(card, testFilterValues),
        );
        if (!hasMatch) {
          disabledValues.add(val);
        }
      });

      this.updateCheckboxOptions(group, sorted, disabledValues);
    });
  }

  updateCheckboxOptions(group, sorted, disabledValues = new Set()) {
    const idBase =
      group.dataset.filterIdBase ?? `filter-${group.dataset.filterAttr}`;
    const optionsEl = group.querySelector(selectors.filterOptions);
    if (!optionsEl) {
      return;
    }

    const checkedValues = this.getCheckedValuesForGroup(group);

    optionsEl.innerHTML = '';

    sorted.forEach(val => {
      const inputId = `${idBase}-${val
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9-]/gi, '')
        .toLowerCase()}`;
      const isDisabled = disabledValues.has(val);
      const li = document.createElement('li');
      li.className = 'gap-2 flex items-center';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'peer disabled:opacity-40';
      input.name = idBase;
      input.value = val;
      input.id = inputId;
      input.checked = checkedValues.includes(val);
      input.disabled = isDisabled;

      const label = document.createElement('label');
      label.className = 'type-body-xs peer-disabled:opacity-40 cursor-pointer';
      label.htmlFor = inputId;
      label.textContent = val;

      li.appendChild(input);
      li.appendChild(label);
      optionsEl.appendChild(li);
    });

    group.style.opacity = '';
    group.style.pointerEvents = '';
  }

  getCheckedValuesForGroup(group) {
    const optionsEl = group.querySelector(selectors.filterOptions);
    if (!optionsEl) {
      return [];
    }
    const checkboxes = optionsEl.querySelectorAll(
      'input[type="checkbox"]:checked',
    );
    return [...checkboxes].map(cb => cb.value);
  }

  setupFilter() {
    this.el.addEventListener('change', e => {
      if (!e.target.matches('input[type="checkbox"]')) {
        return;
      }
      if (e.target.matches(selectors.addToCompare)) {
        return;
      }
      if (e.target.matches(selectors.showComparisons)) {
        return;
      }

      const group = e.target.closest(selectors.filterGroup);
      if (!group) {
        return;
      }

      this.syncFilterCheckboxes(group, e.target);
      this.visibleLimit = this.initialVisible;
      this.populateFilters();
      this.applyVisibility();
    });
  }

  /** Sync the changed checkbox to the corresponding checkbox in other filter groups (sidebar vs drawer) */
  syncFilterCheckboxes(changedGroup, changedCheckbox) {
    const attr = changedGroup.dataset.filterAttr;
    const value = changedCheckbox.value;
    const checked = changedCheckbox.checked;

    if (!attr) {
      return;
    }

    this.el.querySelectorAll(selectors.filterGroup).forEach(group => {
      if (group !== changedGroup && group.dataset.filterAttr === attr) {
        const otherCheckbox = group.querySelector(
          `input[type="checkbox"][value="${CSS.escape(value)}"]`,
        );
        if (otherCheckbox) {
          otherCheckbox.checked = checked;
        }
      }
    });
  }

  getCompareIds() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  saveCompareIds(ids) {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
    } catch {
      // ignore
    }
  }

  setupCompare() {
    const grid = this.el.querySelector(selectors.grid);
    if (!grid) {
      return;
    }

    const compareIds = this.getCompareIds();

    grid.querySelectorAll(selectors.card).forEach(card => {
      const id = card.dataset.styleCardId;
      if (!id) {
        return;
      }
      const checkbox = card.querySelector(selectors.addToCompare);
      if (checkbox) {
        checkbox.checked = compareIds.includes(id);
      }
    });

    this.el.addEventListener('change', e => {
      if (!e.target.matches(selectors.addToCompare)) {
        return;
      }
      const id = e.target.dataset.styleCardId;
      if (!id) {
        return;
      }
      let ids = this.getCompareIds();
      if (e.target.checked) {
        ids = ids.includes(id) ? ids : [...ids, id];
      } else {
        ids = ids.filter(i => i !== id);
      }
      this.saveCompareIds(ids);
      this.applyVisibility();
    });
  }

  setupShowComparisons() {
    const toggle = this.el.querySelector(selectors.showComparisons);
    toggle?.addEventListener('change', () => {
      this.applyVisibility();
    });
  }

  setupQuickshop() {
    this.el.addEventListener('click', e => {
      const trigger = e.target.closest(selectors.quickshopTrigger);
      if (!trigger) {
        return;
      }

      const productUrl = trigger.dataset.productUrl;
      if (productUrl) {
        e.preventDefault();
        openQuickshop(productUrl);
      }
    });
  }

  isShowComparisonsOn() {
    const toggle = this.el.querySelector(selectors.showComparisons);
    return toggle?.checked ?? false;
  }

  getFilterValues() {
    const values = {};
    this.el.querySelectorAll(selectors.filterGroup).forEach(group => {
      const attr = group.dataset.filterAttr;
      if (attr) {
        values[attrToCamel(attr)] = this.getCheckedValuesForGroup(group);
      }
    });
    return values;
  }

  loadCardImages(card) {
    card.querySelectorAll(selectors.lazyImg).forEach(img => {
      const fullSrc = img.dataset.src;
      if (!fullSrc) {
        return;
      }

      if (img.src.includes('placeholder') && img.dataset.srcTiny) {
        img.src = img.dataset.srcTiny;
        delete img.dataset.srcTiny;
      }
      const preload = new Image();
      preload.onload = () => {
        img.src = fullSrc;
        img.classList.remove('blur-sm');
        delete img.dataset.src;
      };
      preload.src = fullSrc;
    });

    this.loadCardHoverVideo(card);
  }

  loadCardHoverVideo(card) {
    const container = card.querySelector(selectors.hoverVideo);
    if (!container || container.dataset.hoverVideoReady === 'true') {
      return;
    }
    const src = container.dataset.hoverVideoSrc;
    if (!src) {
      return;
    }

    const video = document.createElement('video');
    video.src = src;
    video.muted = true;
    video.loop = true;
    video.playsInline = true;
    video.preload = 'none';
    video.setAttribute('aria-hidden', 'true');

    const onEnter = () => {
      video.play().catch(() => {});
    };
    const onLeave = () => {
      video.pause();
    };

    card.addEventListener('mouseenter', onEnter);
    card.addEventListener('mouseleave', onLeave);

    container.appendChild(video);
    container.dataset.hoverVideoReady = 'true';
  }

  setupViewMore() {
    const btn = this.el.querySelector(selectors.viewMoreBtn);
    btn?.addEventListener('click', () => {
      this.visibleLimit += this.loadMoreCount;
      this.applyVisibility();
    });
  }

  applyVisibility() {
    const grid = this.el.querySelector(selectors.grid);
    if (!grid) {
      return;
    }

    const filterValues = this.getFilterValues();
    const hasActiveFilters = Object.values(filterValues).some(v =>
      Array.isArray(v) ? v.length > 0 : Boolean(v),
    );
    const cards = grid.querySelectorAll(selectors.card);
    let matchIndex = 0;
    let totalMatching = 0;

    const showComparisons = this.isShowComparisonsOn();
    const compareIds = this.getCompareIds();

    cards.forEach(card => {
      const cardId = card.dataset.styleCardId;
      const inCompareList = compareIds.includes(cardId);
      const matches = showComparisons
        ? inCompareList
        : this.cardMatchesFilters(card, filterValues);

      if (matches) {
        totalMatching++;
        matchIndex++;
        const visible = matchIndex <= this.visibleLimit;
        card.classList.toggle('hidden', !visible);
        if (visible) {
          card.removeAttribute('data-hidden-by');
          this.loadCardImages(card);
        } else {
          card.setAttribute('data-hidden-by', 'pagination');
        }
      } else {
        card.classList.add('hidden');
        card.setAttribute('data-hidden-by', 'filter');
      }
    });

    const noResults = this.el.querySelector(selectors.noResults);
    const showComparisonsToggle = this.el.querySelector(
      selectors.showComparisons,
    );

    noResults?.classList.toggle(
      'hidden',
      showComparisons || !hasActiveFilters || totalMatching > 0,
    );
    if (showComparisonsToggle) {
      const isEmpty = compareIds.length === 0;
      showComparisonsToggle.disabled = isEmpty;
      if (isEmpty && showComparisonsToggle.checked) {
        showComparisonsToggle.checked = false;
        this.applyVisibility();
      }
    }

    const viewMore = this.el.querySelector(selectors.viewMore);
    viewMore?.classList.toggle('hidden', totalMatching <= this.visibleLimit);

    const viewingNum = this.el.querySelector(selectors.viewingNum);
    const viewingTotals = this.el.querySelectorAll(selectors.viewingTotal);
    if (viewingNum) {
      viewingNum.textContent = Math.min(totalMatching, this.visibleLimit);
    }
    viewingTotals.forEach(el => {
      el.textContent = totalMatching;
    });

    this.updateHoverRotationHint(grid);
    AOS.refresh();
  }

  updateHoverRotationHint(grid) {
    const hints = grid.querySelectorAll(selectors.hoverRotationHint);
    hints.forEach(hint => {
      hint.classList.add('hidden');
      hint.classList.remove('md:flex');
    });

    const firstVisible = [...grid.querySelectorAll(selectors.card)].find(
      card =>
        !card.classList.contains('hidden') &&
        card.querySelector(selectors.hoverRotationHint),
    );
    const hint = firstVisible?.querySelector(selectors.hoverRotationHint);
    if (hint) {
      hint.classList.add('md:flex');
    }
  }
}
